/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { RadioGroup, Typography2 } from '@coursera/cds-core';

import WidgetListLoader from 'bundles/author-questions/components/editors/widget/WidgetListLoader';
import WidgetSelectionListHeader from 'bundles/author-questions/components/editors/widget/WidgetSelectionListHeader';
import WidgetSelectionListItem from 'bundles/author-questions/components/editors/widget/WidgetSelectionListItem';
import type { AuthoringWidgetSummariesV1 } from 'bundles/widget-admin/types/WidgetTypes';

import _t from 'i18n!nls/author-widget';

const styles = {
  root: css`
    min-height: 250px;
  `,
  container: css`
    display: flex;
    height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary);
    border-top: 1px solid var(--cds-color-neutral-stroke-primary);
  `,
  emptyList: css`
    text-align: center;
    padding-top: var(--cds-spacing-600);
  `,
};

type PropsFromCaller = {
  onWidgetSelected: (id: string) => void;
  searchKeyword: string;
  selectedWidgetId: string;
  widgets: AuthoringWidgetSummariesV1[];
  emptyMessage?: string;
  isWidgetListLoaded: boolean;
};

type Props = {
  isWidgetListLoaded: boolean;
} & PropsFromCaller;

const WidgetSelectionList: React.FC<Props> = ({
  onWidgetSelected,
  searchKeyword,
  selectedWidgetId,
  widgets,
  emptyMessage = _t('No plugins are currently available for use.'),
  isWidgetListLoaded,
}) => {
  const isEmpty = widgets.length === 0;

  const renderList = () => {
    if (isWidgetListLoaded && isEmpty) {
      return (
        <Typography2 component="p" variant="bodyPrimary" css={styles.emptyList}>
          {emptyMessage}
        </Typography2>
      );
    }

    if (!isWidgetListLoaded) {
      return <WidgetListLoader />;
    }

    return (
      <div data-testid="widgetSelectionList">
        <WidgetSelectionListHeader />

        <RadioGroup css={styles.container} aria-label={_t('Select plugin')} value={selectedWidgetId}>
          {widgets.map((widget) => {
            const widgetName = widget.name.toLowerCase();
            if (widgetName.indexOf(searchKeyword) === -1) {
              return null;
            }
            return <WidgetSelectionListItem key={widget.id} widget={widget} onWidgetSelected={onWidgetSelected} />;
          })}
        </RadioGroup>
      </div>
    );
  };

  return <div css={styles.root}>{renderList()}</div>;
};
export default WidgetSelectionList;
